<div class="feature-editor-container">
  <div class="loading-overlay" *ngIf="loading">
    <mat-spinner diameter="40"></mat-spinner>
    <p>{{ isEditMode ? 'Saving feature changes...' : 'Creating new feature...' }}</p>
  </div>

  <form [formGroup]="featureForm" class="feature-form">
    <!-- Basic Information -->
    <div class="section-card">
      <div class="section-header">
        <h3>Basic Information</h3>
      </div>
      <div class="section-content">
        <div class="form-row two-columns">
          <mat-form-field appearance="outline">
            <mat-label>Feature Name</mat-label>
            <input matInput formControlName="name" placeholder="e.g., New Dashboard" />
            <mat-error *ngIf="featureForm.get('name')?.invalid">
              {{ getFieldError('name') }}
            </mat-error>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Category</mat-label>
            <mat-select formControlName="category">
              <mat-option value="Book">Book</mat-option>
              <mat-option value="General">General</mat-option>
              <mat-option value="Key">Key</mat-option>
              <mat-option value="KeySystem">Key System</mat-option>
              <mat-option value="Structure">Structure</mat-option>
            </mat-select>
            <mat-error *ngIf="featureForm.get('category')?.invalid">
              {{ getFieldError('category') }}
            </mat-error>
          </mat-form-field>
        </div>

        <div class="form-row">
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Description</mat-label>
            <textarea
              matInput
              formControlName="description"
              rows="2"
              placeholder="e.g., Redesigned dashboard with improved UX"
            ></textarea>
            <mat-error *ngIf="featureForm.get('description')?.invalid">
              {{ getFieldError('description') }}
            </mat-error>
          </mat-form-field>
        </div>
      </div>
    </div>

    <!-- Status -->
    <div class="section-card">
      <div class="section-header">
        <h3>
          Status
          <mat-icon
            class="info-icon"
            matTooltip="Control who can access this feature by setting its status. Use Limited Access for testing, then Full Access for production."
            matTooltipPosition="right">
            info_outline
          </mat-icon>
        </h3>
      </div>
      <div class="section-content">
        <div class="form-row">
          <mat-label><strong>Current Status</strong> (Active Now)</mat-label>
          <mat-radio-group formControlName="status" class="radio-group">
            <mat-radio-button
              value="Disabled"
              matTooltip="Off for everyone"
              matTooltipPosition="right">
              Disabled
            </mat-radio-button>
            <mat-radio-button
              value="Limited Access"
              matTooltip="SuperUsers + specific users (internal testing)"
              matTooltipPosition="right">
              Limited Access
            </mat-radio-button>
            <mat-radio-button
              value="Full Access"
              matTooltip="On for everyone"
              matTooltipPosition="right">
              Full Access
            </mat-radio-button>
          </mat-radio-group>

          <!-- Pilot Validation Error -->
          <div class="pilot-validation-error" *ngIf="pilotValidationError">
            <mat-icon>error</mat-icon>
            <div class="error-content">
              <strong>Limited Access mode requires targeting rules</strong>
              <p>Add specific users below OR keep the SuperUser role.</p>
            </div>
          </div>
        </div>

        <!-- Rollout Percentage - COMMENTED OUT (not using gradual rollout currently) -->
        <!-- Uncomment this section when gradual rollout is needed -->
        <!--
        <div class="form-row">
          <mat-label>Rollout Percentage: {{ featureForm.get('rolloutPercentage')?.value }}%</mat-label>
          <mat-slider
            min="0"
            max="100"
            step="5"
            showTickMarks
            discrete
            class="rollout-slider"
          >
            <input
              matSliderThumb
              formControlName="rolloutPercentage"
            />
          </mat-slider>
        </div>
        -->
      </div>
    </div>

    <!-- Role-Based Access -->
    <div class="section-card collapsible" *ngIf="featureForm.get('status')?.value === 'Limited Access'">
      <div class="section-header" (click)="toggleRolesSection()">
        <h3>
          Role-Based Access
          <span class="section-summary" *ngIf="!isRolesExpanded && getRolesSummary()">{{ getRolesSummary() }}</span>
        </h3>
        <mat-icon class="collapse-icon" [class.expanded]="isRolesExpanded">
          expand_more
        </mat-icon>
      </div>
      <div class="section-content" [class.collapsed]="!isRolesExpanded">
        <!-- Role Feedback Message -->
        <div class="role-feedback-message"
             *ngIf="roleFeedbackMessage"
             [ngClass]="'feedback-' + roleFeedbackType">
          <mat-icon>
            {{ roleFeedbackType === 'success' ? 'check_circle' :
               roleFeedbackType === 'info' ? 'info' :
               roleFeedbackType === 'warning' ? 'warning' : 'error' }}
          </mat-icon>
          <span>{{ roleFeedbackMessage }}</span>
        </div>

        <div class="roles-list" *ngIf="roleRules.length > 0">
          <div class="role-chip" *ngFor="let roleRule of roleRules">
            <mat-icon class="role-icon">shield</mat-icon>
            <span class="role-name">{{ roleRule.role }}</span>
            <button
              *ngIf="!(roleRule.role === 'SuperUser' && featureForm.get('status')?.value === 'Limited Access')"
              type="button"
              mat-icon-button
              class="remove-btn"
              (click)="removeRole(roleRule.role)"
              matTooltip="Remove role">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>

        <div class="no-roles" *ngIf="roleRules.length === 0">
          <mat-icon>group</mat-icon>
          <p>No roles added. SuperUser auto-added in Limited Access mode.</p>
        </div>

        <!-- Show "Add SuperUser Role" button when SuperUser is not already added -->
        <div class="add-superuser-section" *ngIf="!hasSuperUserRole()">
          <button type="button" mat-raised-button color="primary" (click)="addSuperUserRole()">
            <mat-icon>add</mat-icon>
            Add SuperUser Role
          </button>
        </div>
      </div>
    </div>

    <!-- Specific Users -->
    <div class="section-card collapsible" *ngIf="featureForm.get('status')?.value === 'Limited Access'">
      <div class="section-header" (click)="toggleUsersSection()">
        <h3>
          Specific Users
          <span class="optional-badge">Optional</span>
          <span class="section-summary" *ngIf="!isUsersExpanded && getUsersSummary()">{{ getUsersSummary() }}</span>
        </h3>
        <mat-icon class="collapse-icon" [class.expanded]="isUsersExpanded">
          expand_more
        </mat-icon>
      </div>
      <div class="section-content" [class.collapsed]="!isUsersExpanded">
        <p class="section-hint">
          Grant access to specific users in addition to SuperUsers.
        </p>

        <div class="add-user-section">
          <mat-form-field appearance="outline" class="user-select-field">
            <mat-label>Select User</mat-label>
            <mat-select [(ngModel)]="selectedUserId" [ngModelOptions]="{standalone: true}">
              <mat-option *ngFor="let user of allUsers" [value]="user.id">
                {{ user.firstName }} {{ user.lastName }}
              </mat-option>
            </mat-select>
          </mat-form-field>
          <button type="button" mat-raised-button color="primary" (click)="addUser()" [disabled]="!selectedUserId">
            <mat-icon>add</mat-icon>
            Add User
          </button>
        </div>

        <!-- User Feedback Message -->
        <div class="user-feedback-message"
             *ngIf="userFeedbackMessage"
             [ngClass]="'feedback-' + userFeedbackType">
          <mat-icon>
            {{ userFeedbackType === 'success' ? 'check_circle' :
               userFeedbackType === 'info' ? 'info' :
               userFeedbackType === 'warning' ? 'warning' : 'error' }}
          </mat-icon>
          <span>{{ userFeedbackMessage }}</span>
        </div>

        <div class="users-list" *ngIf="specificUsers.length > 0">
          <div class="user-chip" *ngFor="let userTarget of specificUsers">
            <mat-icon class="user-icon">person</mat-icon>
            <span class="user-name">{{ getUserDisplayName(userTarget) }}</span>
            <span class="user-email-hint">{{ userTarget.email }}</span>
            <button type="button" mat-icon-button class="remove-btn" (click)="removeUser(userTarget)" matTooltip="Remove user">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        </div>

        <div class="no-users" *ngIf="specificUsers.length === 0">
          <mat-icon>people_outline</mat-icon>
          <p>No users added. Feature available to all users (based on status).</p>
        </div>
      </div>
    </div>

    <!-- Bottom Action Buttons -->
    <div class="bottom-actions">
      <button type="button" mat-button class="cancel-btn" (click)="cancel()" [disabled]="loading">Cancel</button>
      <button type="button" mat-raised-button color="primary" (click)="save()" [disabled]="loading || featureForm.invalid || (isEditMode && !hasUnsavedChanges())">
        <mat-icon>save</mat-icon>
        {{ isEditMode ? 'Update' : 'Create' }}
      </button>
    </div>

  </form>
</div>
